import React, { useState, useRef, useMemo } from 'react';

import {
  FloatingPortal,
  FloatingArrow,
  arrow,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
  useClick,
} from '@floating-ui/react';

type TooltipProps = {
  tooltipAnchor: React.ReactNode;
  tooltipContent: React.ReactNode;
};

export const Tooltip: React.FC<TooltipProps> = ({ tooltipAnchor, tooltipContent }) => {
  // Tooltip
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Arrow
  const arrowRef = useRef(null);

  // Declare Tooltip
  const {
    refs: tooltipRefs,
    floatingStyles: tooltipFloatingStyles,
    context: tooltipContext,
    placement: tooltipPlacement,
  } = useFloating({
    placement: 'top',
    open: tooltipOpen,
    onOpenChange: setTooltipOpen,
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(12),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
    nodeId: 'tooltip',
  });

  const { getReferenceProps: getTooltipReferenceProps, getFloatingProps: getTooltipFloatingProps } = useInteractions([
    useHover(tooltipContext, { delay: { close: 200 } }),
    useClick(tooltipContext, { ignoreMouse: false, toggle: true }),
    useRole(tooltipContext, { role: 'tooltip' }),
    useDismiss(tooltipContext),
  ]);

  return (
    <>
      <span
        ref={tooltipRefs.setReference}
        {...getTooltipReferenceProps({
          onClick: (e) => {
            e.stopPropagation();
          },
        })}
      >
        {tooltipAnchor}
      </span>

      {tooltipOpen && (
        <FloatingPortal>
          <div
            ref={tooltipRefs.setFloating}
            style={{
              ...tooltipFloatingStyles,
              maxWidth: '220px',
              padding: '3px 10px',
              fontSize: '0.8125rem',
              color: '#333',
              backgroundColor: '#fff5d5',
              border: '1px solid #fc0',
              borderRadius: '3px',
              boxShadow: '0 2px 10px rgb(0 0 0 / 15%)',
              textAlign: 'center',
              zIndex: '105',
            }}
            {...getTooltipFloatingProps()}
          >
            {tooltipContent}
            <FloatingArrow
              ref={arrowRef}
              context={tooltipContext}
              fill="#fff5d5"
              stroke="#fc0"
              strokeWidth={1}
              style={tooltipPlacement === 'top' ? { transform: 'translateY(-1px)' } : {}}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
